import * as React from 'react'
import Helmet from 'react-helmet'
import { connect, ConnectedProps } from 'react-redux'
import { match as urlMatch } from 'react-router'
import { SiteCountry, SiteLanguage } from '../data/models/locale'
import { AppState } from '../data/state'
import { SRPRS_DEV } from '../env'
import { getLink, getLinkFromName, getLinkName } from '../routing/links'

import { getCdnUrl } from '../data/api/cdn'
import { seo as SEOValues } from '../data/seo'

interface SEOValue {
  title?: string
  description?: string
  imageUrl?: string
  canonicalPage?: string
}

const DEFAULT_SEO_VALUES: SEOValue = {
  title: 'srprs.me - Explore the unknown and take a city trip with srprs.me',
  description:
    'Though we can’t break the news yet on where you will end up, know that it all starts here.',
  imageUrl: getCdnUrl('og-home.png'),
}

const getSEOValues = (pageName: string, country: SiteCountry): SEOValue => {
  const pageSlug: keyof typeof SEOValues = SEOValues.hasOwnProperty(pageName)
    ? (pageName as keyof typeof SEOValues)
    : `__default__`
  const seoPage: Partial<{ [key in SiteCountry]: SEOValue }> =
    SEOValues[pageSlug]

  const langSlug =
    country === SiteCountry.be &&
    !seoPage.hasOwnProperty(country) &&
    seoPage.hasOwnProperty(SiteCountry.nl)
      ? SiteCountry.nl
      : country === SiteCountry.ie &&
        !seoPage.hasOwnProperty(country) &&
        seoPage.hasOwnProperty(SiteCountry.uk)
      ? SiteCountry.uk
      : country

  if (seoPage.hasOwnProperty(langSlug)) {
    return seoPage[langSlug]!
  }
  return DEFAULT_SEO_VALUES
}

interface SEOProps extends SEOValue, PropsFromRedux {
  match: urlMatch<any>
  pageName: string
}

enum SEOLanguage {
  nl = 'nl',
  nlNl = 'nl-NL',
  nlBe = 'nl-BE',
  en = 'en',
  enGb = 'en-GB',
  enIE = 'en-IE',
}

const LanguageCountry = new Map([
  [SEOLanguage.nl, SiteCountry.nl],
  [SEOLanguage.nlNl, SiteCountry.nl],
  [SEOLanguage.nlBe, SiteCountry.be],
  [SEOLanguage.en, SiteCountry.uk],
  [SEOLanguage.enGb, SiteCountry.uk],
  [SEOLanguage.enIE, SiteCountry.ie],
])

const seoUrl = (path?: string) => {
  const url = new URL(path || `/`, window.location.href)
  return url.toString()
}

const getMetaTags = (
  pageName: string,
  country: SiteCountry,
  match: urlMatch<any>,
) => {
  const pageSEOValues = getSEOValues(pageName, country)

  const title =
    pageSEOValues.title !== undefined
      ? pageSEOValues.title
      : DEFAULT_SEO_VALUES.title
  const description =
    pageSEOValues.description !== undefined
      ? pageSEOValues.description
      : DEFAULT_SEO_VALUES.description
  const imageUrl =
    pageSEOValues.imageUrl !== undefined
      ? pageSEOValues.imageUrl
      : DEFAULT_SEO_VALUES.imageUrl

  return [
    { name: 'description', content: description },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@srprsme' },
    { name: 'twitter:title', content: `${title}` },
    { name: 'twitter:description', content: description },
    { name: 'twitter:creator', content: '@srprsme' },
    { name: 'twitter:image:src', content: imageUrl },
    { name: 'og:title', content: `${title}` },
    { name: 'og:description', content: description },
    { name: 'og:image', content: imageUrl },
    { name: 'og:url', content: seoUrl(match.url) },
    { name: 'og:type', content: 'website' },
    { name: 'og:site_name', content: 'srprs.me' },
    { name: 'fb:admins', content: '100001832405717' },
    { name: 'fb:pages', content: '542489652461256' },
  ]
}

const getAltLinks = (country: string, name: string, params?: any) => {
  const alts: SEOLanguage[] = Object.values(SEOLanguage).filter(
    x => LanguageCountry.get(x) !== country,
  )
  return alts.map(a => {
    const aCountry = LanguageCountry.get(a) as SiteCountry
    return {
      rel: 'alternative',
      href: seoUrl(getLink(aCountry, name, params)),
      hreflang: a.toString(),
    }
  })
}

class SEO extends React.Component<SEOProps> {
  public componentDidMount() {
    if (SRPRS_DEV) {
      const link = document.querySelector("link[rel*='icon']")
      if (link) {
        document.head.removeChild(link)
      }
    }
  }

  public render() {
    const { match, pageName, lang, country } = this.props
    const name = getLinkName(match.path)
    const altLinks = getAltLinks(country, name, match.params)
    const pageSEOValues = getSEOValues(pageName, country)
    const canonicalUrl =
      pageSEOValues.canonicalPage !== undefined
        ? getLinkFromName(pageSEOValues.canonicalPage)
        : seoUrl(match.url)
    let title =
      pageSEOValues.title !== undefined
        ? pageSEOValues.title
        : DEFAULT_SEO_VALUES.title
    if (SRPRS_DEV) {
      title = '(DEV) ' + title
    }

    return (
      <Helmet
        htmlAttributes={{ lang }}
        title={title}
        link={[{ rel: 'canonical', href: canonicalUrl }].concat(altLinks)}
        meta={getMetaTags(pageName, country, match)}
      >
        {SRPRS_DEV ? (
          <link
            rel="icon"
            href={getCdnUrl('green-favicon.ico')}
            type="image/x-icon"
          />
        ) : null}
      </Helmet>
    )
  }
}

const connector = connect((state: AppState) => ({
  lang: state.locale.language,
  country: state.locale.country,
}))
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(SEO)
